import { useCallback, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useDebounce } from './useDebounce';
import { ASSET_STUDIO, ASSETS_PREVIEW, ASSETS_PREVIEW_INNER_TABS, ASSETS_TYPE, FILTER_PARAMS } from '@/types/shared/navigation-tabs.enums';
import { useQueryNavigation } from './useNavigateWithQuery';
const assetsTypeWhitelist = [ASSETS_TYPE.FIXED, ASSETS_TYPE.MOBILE];

export function useAppFilters() {
  const { currentParams, setSearchParams, pathname } = useQueryNavigation();
  const { siteId } = useParams();
  const {
    assetsType,
    assetsPreview,
    tab,
    search,
    assetStudio,
    selectedComponent,
    selectedFormula,
    selectedPart,
    selectedAsset,
    id,
    selectedDiagnostic,
    assetsSection,
    evidenceResourceId,
  } = currentParams;
  const setFilters = useCallback(
    (filters: Partial<Record<FILTER_PARAMS, string | null>>) => {
      setSearchParams((params) => {
        Object.entries(filters).forEach(([key, value]) => {
          if (value === undefined || value === null) {
            params.delete(key);
          } else {
            params.set(key, value);
          }
        });
        // Handle edge cases for `assetsType` and `assetsPreview`
        if (filters.assetsPreview && !assetsTypeWhitelist.includes(assetsType as ASSETS_TYPE)) {
          params.set(FILTER_PARAMS.ASSETS_TYPE, ASSETS_TYPE.FIXED);
        }
        return params;
      });
    },
    [setSearchParams, assetsTypeWhitelist]
  );

  const shouldResetPreview = siteId && (assetsType === ASSETS_TYPE.MOBILE || assetsType === ASSETS_TYPE.FIXED);

  // Reset assetsPreview whenever assetsType changes to ensure tab compatibility
  useOnChange(assetsType, () => {
    if (shouldResetPreview) {
      // Operator Proficiency tab is only available for Mobile assets
      // When switching to Fixed Assets with Operator Proficiency selected, reset to default "assets" tab
      if (assetsPreview === ASSETS_PREVIEW.OPERATOR_PROFICIENCY && assetsType === ASSETS_TYPE.FIXED) {
        setFilters({ [FILTER_PARAMS.ASSETS_PREVIEW]: ASSETS_PREVIEW.ASSETS });
      }
    }
  });

  // Initialize default filter values based on the current route
  useEffect(() => {
    if (pathname.includes('sites')) {
      if (!assetsType) {
        setFilters({
          [FILTER_PARAMS.ASSETS_TYPE]: siteId ? ASSETS_TYPE.FIXED : ASSETS_TYPE.ALL,
        });
      }
      if (siteId && !assetsPreview) {
        setFilters({
          [FILTER_PARAMS.ASSETS_PREVIEW]: ASSETS_PREVIEW.ASSETS,
          [FILTER_PARAMS.SELECTED_ASSET]: ASSETS_PREVIEW.ALL_ASSETS,
        });
      }
      if (siteId && assetsPreview && !assetStudio && selectedComponent) {
        setFilters({ [FILTER_PARAMS.ASSET_STUDIO]: ASSET_STUDIO.DIAGNOSTICS });
      }
      if (assetStudio && !selectedComponent) {
        setFilters({ [FILTER_PARAMS.SELECTED_COMPONENT]: ASSET_STUDIO.ALL_COMPONENTS });
      }

      // if (assetStudio && !selectedFormula) setFilters({ [FILTER_PARAMS.SELECTED_FORMULA]: ASSET_STUDIO.ALL_FORMULAS });

      // Reset selectedAsset to 'all' when on Assets tab with Faults section
      if (
        assetsPreview === ASSETS_PREVIEW.ASSETS &&
        assetsSection === ASSETS_PREVIEW_INNER_TABS.FAULTS &&
        selectedAsset &&
        selectedAsset !== ASSETS_PREVIEW.ALL_ASSETS
      ) {
        setFilters({ [FILTER_PARAMS.SELECTED_ASSET]: ASSETS_PREVIEW.ALL_ASSETS });
      }
    }
  }, [pathname, siteId, assetsType, assetsPreview, assetStudio, selectedComponent, selectedAsset, assetsSection, setFilters]);

  return {
    tab,
    assetsPreview,
    assetsType,
    assetStudio,
    search,
    selectedComponent,
    selectedAsset,
    selectedFormula,
    selectedPart,
    selectedDiagnostic,
    assetsSection,
    evidenceResourceId,
    id,
    setFilters,
  };
}

function useOnChange<T>(value: T, callback: () => void) {
  const prevRef = useRef<T | null>(null);

  useEffect(() => {
    if (prevRef.current !== null && prevRef.current !== value) {
      callback();
    }
    prevRef.current = value;
  }, [value, callback]);
}
